import { Accordion, ButtonText } from '@morressier/alembic';
import {
  faArrowsRotate,
  faBell,
  faHourglassHalf
} from '@fortawesome/pro-regular-svg-icons';
import { EditorialRevisionContextModal } from '../../../modals/editorial/RevisonContext';
import { useState } from 'react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';
import { ExtendSubmissionDeadlineModal } from '../../../decisionModals/GrantSubmissionExtensionModal';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { IconBadge } from 'src/components/IconBadge';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';

export const EditorialRevisionAdditionalInfo = () => {
  const {
    submissionId,
    submissionContextId,
    workflowResponse,
    organizationId
  } = useSubmissionData();
  const { workflowData } = workflowResponse;
  const deadline = workflowData.currentDeadline;
  const [showReviewContextModal, setShowReviewContextModal] = useState(false);
  const [showGrantExtensionModal, setShowGrantExtensionModal] = useState(false);

  const isExtensionRequested =
    workflowData.deadlineExtensions?.status === 'requested';
  const isDeadlineOverdue =
    calculateDaysToDeadline(new Date(workflowData.currentDeadline ?? '')) < 1;

  return (
    <AdditionalInformationWrapper>
      <Accordion
        chakraAccordionProps={{
          width: '100%',
          defaultIndex: 0,
          allowToggle: true
        }}
        items={[
          {
            title: (
              <Flex alignItems={'flex-start'} gap={2}>
                <IconBadge icon={faBell} variant="neutral" />
                <Flex flexDirection={'column'} marginLeft={2}>
                  <Text textStyle="headingItem">Highlights</Text>
                  <Text textStyle="textContext">
                    Review key updates and other relevant information in this
                    phase.
                  </Text>
                </Flex>
              </Flex>
            ),
            content: (
              <Flex flexDirection="column" width="100%" gap={4}>
                <EditorialAuthorWithdrawalRequested />

                <Flex gap={3} alignItems="center">
                  <IconBadge icon={faArrowsRotate} variant="attention" />
                  <Text textStyle="headingItem">
                    Submission under Author Revision
                  </Text>
                  <ButtonText
                    marginLeft="auto"
                    onClick={() => setShowReviewContextModal(true)}
                  >
                    View Details
                  </ButtonText>
                </Flex>

                {(isExtensionRequested || isDeadlineOverdue) && (
                  <>
                    <Flex
                      borderTop="1px solid"
                      borderColor="neutralPrimaryBorder"
                    />
                    <Flex width="100%" gap={3} alignItems="start">
                      {isExtensionRequested ? (
                        <>
                          <IconBadge
                            icon={faHourglassHalf}
                            variant="attention"
                          />
                          <Flex flexDirection="column" flex={1}>
                            <Text textStyle="headingItem">
                              Author Revision Extension Requested
                            </Text>
                          </Flex>
                        </>
                      ) : (
                        <>
                          <Tooltip
                            label="The author will be unable to submit a revised
                              manuscript unless a deadline extension is given."
                            aria-label="Info icon tooltip"
                            placement="top"
                            hasArrow
                            shouldWrapChildren
                          >
                            <IconBadge
                              icon={faArrowsRotate}
                              variant="caution"
                            />
                          </Tooltip>
                          <Flex flexDirection="column" flex={1}>
                            <Text textStyle="headingItem">
                              Revision Deadline Overdue
                            </Text>
                          </Flex>
                        </>
                      )}
                      <ButtonText
                        marginLeft="auto"
                        onClick={() => setShowGrantExtensionModal(true)}
                      >
                        Review Extension Request
                      </ButtonText>
                    </Flex>
                  </>
                )}
              </Flex>
            )
          }
        ]}
      />

      {showReviewContextModal && (
        <EditorialRevisionContextModal
          submissionId={submissionId}
          onClose={() => setShowReviewContextModal(false)}
          deadline={deadline}
          role="editorial"
        />
      )}

      {showGrantExtensionModal && (
        <ExtendSubmissionDeadlineModal
          onClose={() => setShowGrantExtensionModal(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          isExtensionRequested={isExtensionRequested}
          proposedDate={workflowData.deadlineExtensions?.proposedDate}
        />
      )}
    </AdditionalInformationWrapper>
  );
};
